import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Empty, Flex, Grid, Progress, Skeleton, Space, Tooltip, Typography, theme } from 'antd';
import { estiloDeDestaque, moduleLabel, type ModuleKey } from '@titularis/ui';
import { api } from '../../api';
import { registrarTela } from '../../analytics';
import { LinhaDeAjuste, SecaoDeAjustes, TituloDaPagina } from '../../components/Ajustes';
import { PRODUTOS } from '../../produtos';
import { contar, palavra } from '../../texto';

/**
 * Desempenho: quanto o aluno respondeu, quanto acertou e em que ritmo.
 *
 * Os três números do topo saem em serifa, grandes. É o mesmo recurso que a
 * vitrine usa no preço, e pela mesma razão: número que se anuncia pede corpo e
 * peso próprios, senão vira célula de planilha. O resto da tela é sans, como
 * todo o produto.
 *
 * **Não há gráfico de biblioteca aqui.** Catorze barras com duas alturas é
 * `div` com altura em porcentagem; trazer um Recharts da vida para isto
 * custaria mais de bundle do que a tela inteira pesa.
 */
const ALTURA_DO_GRAFICO = 96;

export function DesempenhoPage() {
  const { token } = theme.useToken();
  const telas = Grid.useBreakpoint();

  useEffect(() => registrarTela('/configuracoes/desempenho'), []);

  const desempenho = useQuery({
    queryKey: ['study-performance'],
    queryFn: () => api.studyPerformance(),
  });
  const status = useQuery({ queryKey: ['study-status'], queryFn: () => api.studyStatus() });

  if (desempenho.isLoading) return <Skeleton active paragraph={{ rows: 8 }} />;
  if (desempenho.error) {
    return <Alert type="error" showIcon title={(desempenho.error as Error).message} />;
  }

  const dados = desempenho.data;
  if (!dados) return null;

  const maiorDoRitmo = Math.max(1, ...dados.ritmo.map((dia) => dia.total));

  return (
    <Space orientation="vertical" size="large" style={{ width: '100%' }}>
      <TituloDaPagina
        titulo="Desempenho"
        descricao="O que as suas respostas dizem sobre onde você está."
      />

      {dados.total === 0 ? (
        <SecaoDeAjustes titulo="Ainda não há o que somar" descricao="Responda e volte aqui.">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Seu desempenho aparece assim que você responder a primeira questão."
          />
        </SecaoDeAjustes>
      ) : (
        <>
          <SecaoDeAjustes
            titulo="Resumo"
            descricao="Contando tudo o que você já respondeu, desde sempre."
          >
            <Flex gap={telas.sm ? 48 : 24} wrap style={{ paddingTop: 8 }}>
              <Numero
                valor={String(dados.total)}
                rotulo={palavra(dados.total, 'questão respondida', 'questões respondidas')}
              />
              <Numero
                valor={dados.percentual === null ? '-' : `${dados.percentual}%`}
                rotulo={`de acerto (${contar(dados.acertos, 'certa', 'certas')})`}
                destacado
              />
              <Numero
                valor={`${dados.diasEstudados}`}
                rotulo={`${palavra(dados.diasEstudados, 'dia', 'dias')} com estudo nos últimos ${dados.diasDoRitmo}`}
              />
            </Flex>
          </SecaoDeAjustes>

          <SecaoDeAjustes
            titulo="Ritmo"
            descricao={`Quanto você respondeu por dia nos últimos ${dados.diasDoRitmo} dias.`}
          >
            <Flex
              align="flex-end"
              gap={telas.sm ? 8 : 4}
              style={{ height: ALTURA_DO_GRAFICO, paddingTop: 8 }}
              role="img"
              aria-label={`Respostas por dia nos últimos ${dados.diasDoRitmo} dias`}
            >
              {dados.ritmo.map((dia) => {
                const altura = (dia.total / maiorDoRitmo) * 100;
                const erros = dia.total - dia.acertos;

                return (
                  <Tooltip
                    key={dia.dia}
                    title={
                      dia.total === 0
                        ? `${diaCurto(dia.dia)}: sem respostas`
                        : `${diaCurto(dia.dia)}: ${dia.acertos} de ${dia.total}`
                    }
                  >
                    <Flex
                      vertical
                      justify="flex-end"
                      style={{ flex: 1, height: '100%', minWidth: 0 }}
                    >
                      {/*
                        Dia parado não some: vira um traço na base. Barra
                        ausente faria o gráfico parecer ter menos dias do que
                        tem, e o buraco na constância é justamente o que a
                        pessoa precisa enxergar.
                      */}
                      {dia.total === 0 ? (
                        <div
                          style={{
                            height: 2,
                            borderRadius: 1,
                            background: token.colorFillSecondary,
                          }}
                        />
                      ) : (
                        <Flex
                          vertical
                          justify="flex-end"
                          style={{
                            height: `${Math.max(altura, 6)}%`,
                            borderRadius: token.borderRadiusSM,
                            overflow: 'hidden',
                            background: token.colorFillSecondary,
                          }}
                        >
                          {/* O acerto é a parte cheia, e cresce de baixo. */}
                          <div
                            style={{
                              height: `${(dia.acertos / dia.total) * 100}%`,
                              background: token.colorPrimary,
                            }}
                          />
                          {erros > 0 ? <div style={{ flex: 1 }} /> : null}
                        </Flex>
                      )}
                    </Flex>
                  </Tooltip>
                );
              })}
            </Flex>

            <Flex justify="space-between" style={{ paddingTop: 8 }}>
              <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                {diaCurto(dados.ritmo[0]?.dia ?? '')}
              </Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                hoje
              </Typography.Text>
            </Flex>
          </SecaoDeAjustes>

          <SecaoDeAjustes
            titulo="Por módulo"
            descricao="Onde você acerta mais, e onde ainda não."
          >
            {dados.porModulo.map((linha, indice) => (
              <LinhaDeAjuste
                key={linha.module}
                primeira={indice === 0}
                titulo={nomeDoModulo(linha.module)}
                descricao={`${contar(linha.acertos, 'certa', 'certas')} de ${linha.total} ${palavra(linha.total, 'respondida', 'respondidas')}`}
              >
                <Progress
                  percent={linha.percentual ?? 0}
                  size={[telas.sm ? 180 : 120, 8]}
                  strokeColor={PRODUTOS[linha.module]?.acento ?? token.colorPrimary}
                  format={(valor) => `${valor}%`}
                />
              </LinhaDeAjuste>
            ))}
          </SecaoDeAjustes>
        </>
      )}

      {status.data ? (
        <SecaoDeAjustes titulo="Esta semana" descricao="O que você já fez desde segunda-feira.">
          <LinhaDeAjuste primeira titulo="Questões respondidas">
            <Typography.Text>{status.data.usoDaSemana.answers}</Typography.Text>
          </LinhaDeAjuste>
          <LinhaDeAjuste titulo="Resoluções abertas">
            <Typography.Text>{status.data.usoDaSemana.commentaries}</Typography.Text>
          </LinhaDeAjuste>
        </SecaoDeAjustes>
      ) : null}
    </Space>
  );
}

/** Um número do resumo: o valor em serifa, o rótulo em sans, embaixo. */
function Numero({
  valor,
  rotulo,
  destacado,
}: {
  valor: string;
  rotulo: string;
  destacado?: boolean;
}) {
  const { token } = theme.useToken();

  return (
    <div>
      <div
        style={{
          ...estiloDeDestaque(40),
          color: destacado ? token.colorPrimary : token.colorText,
        }}
      >
        {valor}
      </div>
      <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
        {rotulo}
      </Typography.Text>
    </div>
  );
}

/**
 * "2026-08-20" vira "20/08".
 *
 * Cortando a string, e não pelo `Date`: `new Date('2026-08-20')` é meia-noite
 * **UTC**, e no fuso de Brasília isso é 21h do dia 19 - o gráfico inteiro
 * apareceria deslocado em um dia para quem estivesse a oeste de Greenwich.
 */
function diaCurto(dia: string): string {
  const [, mes, numero] = dia.split('-');
  return mes && numero ? `${numero}/${mes}` : dia;
}

/** O nome da fase, pelo dicionário do domínio. */
function nomeDoModulo(chave: string): string {
  return moduleLabel[chave as ModuleKey] ?? chave;
}
